/**
 * One row of a thread, drawn (see `threadRows` for what the rows are).
 *
 * Its own component so every place that draws a thread draws the same rows the
 * same way: a session's pane (`Thread`) and the World view's compact thread
 * (⌘.). A card that says one thing in the pane and another in the strip is two
 * answers to one question.
 */

import type { ReactNode } from 'react'
import {
  CommentActRow,
  type CommentLookup,
  CommentWakeCard,
} from './CommentInThread'
import { Message } from './Message'
import type { StopTrailing } from './stopVerdict'
import type { SubthreadState } from './subthreadState'
import { ThreadCard } from './ThreadCard'
import { ThreadCardGroup } from './ThreadCardGroup'
import type { ToolResult } from './ToolCallBlock'
import { ToolCallCard } from './ToolCallCard'
import type { CardTarget } from './threadCards'
import type { ThreadRow } from './threadRows'
import { VoiceCallRow } from './VoiceCallRow'

export interface ThreadRowProps {
  results: ReadonlyMap<string, ToolResult>
  onOpenDocument?: (documentId: string) => void
  onOpenSession?: (sessionId: string) => void
  resolveTitle?: (target: CardTarget, id: string) => string | undefined
  resolveSessionState?: (sessionId: string) => SubthreadState | undefined
  renderUndo?: (documentId: string, hash: string) => ReactNode
  commentLookup?: CommentLookup
  /** RFC 0348 3e: what the World agent decided about a stop, at its right. */
  stopTrailing?: StopTrailing
}

export function ThreadRowView({
  row,
  results,
  onOpenDocument,
  onOpenSession,
  resolveTitle,
  resolveSessionState,
  renderUndo,
  commentLookup,
  stopTrailing,
}: ThreadRowProps & { row: ThreadRow }) {
  const resolveDocTitle = resolveTitle
    ? (id: string) => resolveTitle('document', id)
    : undefined
  if (row.kind === 'comment') {
    return (
      <CommentWakeCard
        message={row.message}
        {...(row.passed !== undefined ? { passed: row.passed } : {})}
        {...(commentLookup ? { lookup: commentLookup } : {})}
        {...(resolveDocTitle ? { resolveTitle: resolveDocTitle } : {})}
        {...(onOpenDocument ? { onOpenDocument } : {})}
      />
    )
  }
  if (
    row.kind === 'card' &&
    row.call &&
    row.card.target === 'comment' &&
    (row.card.verb === 'Replied' || row.card.verb === 'Commented')
  ) {
    return (
      <CommentActRow
        card={row.card}
        {...(commentLookup ? { lookup: commentLookup } : {})}
        {...(resolveDocTitle ? { resolveTitle: resolveDocTitle } : {})}
        {...(onOpenDocument ? { onOpenDocument } : {})}
      />
    )
  }
  if (row.kind === 'call') {
    return (
      <VoiceCallRow
        lines={row.lines}
        {...(onOpenSession ? { onOpenSession } : {})}
      />
    )
  }
  if (row.kind === 'tools') {
    return (
      <ToolCallCard calls={row.calls} resultFor={(id) => results.get(id)} />
    )
  }
  if (row.kind === 'card' && row.more?.length) {
    return (
      <ThreadCardGroup
        items={[
          {
            card: row.card,
            ...(row.call ? { call: row.call } : {}),
            ...(row.message ? { message: row.message } : {}),
          },
          ...row.more,
        ]}
        {...(stopTrailing ? { stopTrailing } : {})}
        {...(onOpenDocument ? { onOpenDocument } : {})}
        {...(onOpenSession ? { onOpenSession } : {})}
        {...(resolveTitle ? { resolveTitle } : {})}
        {...(resolveSessionState ? { resolveSessionState } : {})}
        {...(renderUndo ? { renderUndo } : {})}
      />
    )
  }
  if (row.kind === 'card') {
    const result = row.call ? results.get(row.call.id) : undefined
    const trailing =
      row.message && stopTrailing ? stopTrailing(row.message) : undefined
    return (
      <ThreadCard
        card={row.card}
        {...(row.call ? { call: row.call } : {})}
        {...(result ? { result } : {})}
        {...(trailing ? { trailing } : {})}
        {...(onOpenDocument ? { onOpenDocument } : {})}
        {...(onOpenSession ? { onOpenSession } : {})}
        {...(resolveTitle ? { resolveTitle } : {})}
        {...(resolveSessionState ? { resolveSessionState } : {})}
        {...(renderUndo ? { renderUndo } : {})}
      />
    )
  }
  return <Message message={row.message} />
}
